import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { attendanceAPI, shiftsAPI } from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { notify, reportLoadError } from '../../utils/notify';
import { can } from '../../utils/permissions';
import { Moon } from 'lucide-react';
import StatusBadge, { statusVariant } from '../../components/StatusBadge';
import { notInSince, shiftTimes } from './plannedModel';
import styles from './TimeAttendance.module.css';

const REFRESH_MS = 60 * 1000;
const AWAY = { absent: 'Absent', sick: 'Sick', leave: 'Leave', vacation: 'Leave' };

// Working and the open night shift are presence; away days take the look
// of their status (absent, sick, leave) as everywhere else.
const STATE_VARIANT = { working: 'success', night: 'success', done: 'neutral', not_in: 'neutral' };

const hhmm = (time) => (time ? String(time).slice(0, 5) : '');
const fullName = (e) => `${e.first_name || ''} ${e.last_name || ''}`.trim();

/**
 * Where an employee is today. `openShift` is yesterday's shift still open
 * (a night shift), which counts as working.
 */
export const dayState = (employee, openShift) => {
  if (AWAY[employee.status]) return 'away';
  if (employee.check_in_time && !employee.check_out_time) return 'working';
  if (employee.check_in_time && employee.check_out_time) return 'done';
  if (openShift) return 'night';
  return 'not_in';
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'not_in', label: 'Not in yet' },
  { key: 'working', label: 'Working' },
  { key: 'done', label: 'Clocked out' },
  { key: 'away', label: 'Absent / sick / leave' },
];

const TodayTab = () => {
  const { t, locale } = useLanguage();
  const { user } = useContext(AuthContext);
  // Clocking needs attendance:create (as the backend asks); typing the time
  // by hand also needs attendance:update.
  const canClock = can(user, 'attendance', 'create');
  const canEditTime = canClock && can(user, 'attendance', 'update');
  // The planned shifts, read only, for who may see the planner
  const canSeePlan = can(user, 'schedules', 'view');

  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // employee id being clocked
  const [times, setTimes] = useState({}); // employee id -> HH:MM typed by hand
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [plan, setPlan] = useState(null); // today's planned shifts; null = not shown

  // The loader must not change with the language function, or the effect
  // below would reload (and restart the refresh timer) on every render.
  const tRef = useRef(t);
  tRef.current = t;
  const load = useCallback(async () => {
    try {
      const res = await attendanceAPI.getToday();
      setBoard(res.data);
    } catch (error) {
      reportLoadError(error, tRef.current('Could not load today\'s attendance'), { main: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  // The plan changes less often than the board: once per day shown
  const boardDate = board?.date;
  useEffect(() => {
    if (!canSeePlan || !boardDate) return undefined;
    let cancelled = false;
    shiftsAPI.list({ from: boardDate, to: boardDate })
      .then((res) => { if (!cancelled) setPlan(res.data.shifts); })
      .catch((error) => reportLoadError(error, tRef.current('Could not load the planned shifts')));
    return () => { cancelled = true; };
  }, [canSeePlan, boardDate]);

  const planOf = (employeeId) => (plan || []).filter((s) => Number(s.employee_id) === Number(employeeId));

  const openByEmployee = useMemo(() => {
    const map = new Map();
    (board?.open_shifts || []).forEach((s) => map.set(Number(s.employee_id), s));
    return map;
  }, [board]);

  const rows = useMemo(() => (board?.employees || []).map((e) => ({
    ...e,
    state: dayState(e, openByEmployee.get(Number(e.id))),
    openShift: openByEmployee.get(Number(e.id)),
  })), [board, openByEmployee]);

  const counts = useMemo(() => rows.reduce((acc, r) => {
    const key = r.state === 'night' ? 'working' : r.state;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {}), [rows]);

  const visible = rows.filter((r) => {
    const state = r.state === 'night' ? 'working' : r.state;
    if (filter !== 'all' && state !== filter) return false;
    const q = search.trim().toLowerCase();
    return !q || fullName(r).toLowerCase().includes(q) || (r.position || '').toLowerCase().includes(q);
  });

  const clock = async (employeeId, direction) => {
    const time = (times[employeeId] || '').trim();
    const payload = { employee_id: employeeId };
    if (time) payload.time = time;
    setBusy(employeeId);
    try {
      if (direction === 'in') await attendanceAPI.clockIn(payload);
      else await attendanceAPI.clockOut(payload);
      const name = fullName(rows.find((r) => Number(r.id) === Number(employeeId))
        || (board?.open_shifts || []).find((s) => Number(s.employee_id) === Number(employeeId)) || {});
      notify(direction === 'in'
        ? t('{name} clocked in at {time}', { name, time: time || t('now') })
        : t('{name} clocked out at {time}', { name, time: time || t('now') }), 'success');
      setTimes((prev) => ({ ...prev, [employeeId]: '' }));
      await load();
    } catch (error) {
      notify(error.response?.data?.message || error.message, 'error');
    } finally {
      setBusy(null);
    }
  };

  const timeInput = (employeeId) => canEditTime && (
    <input
      type="time"
      className={`input ${styles.timeInput}`}
      value={times[employeeId] || ''}
      onChange={(e) => setTimes((prev) => ({ ...prev, [employeeId]: e.target.value }))}
      aria-label={t('Time (empty = now)')}
      title={t('Time (empty = now)')}
    />
  );

  const action = (r) => {
    if (!canClock) return null;
    if (r.state === 'not_in') {
      return (
        <div className={styles.actionCell}>
          {timeInput(r.id)}
          <button type="button" className="btn btn--primary" disabled={busy !== null} onClick={() => clock(r.id, 'in')}>
            {t('Clock in')}
          </button>
        </div>
      );
    }
    if (r.state === 'working' || r.state === 'night') {
      return (
        <div className={styles.actionCell}>
          {timeInput(r.id)}
          <button type="button" className="btn btn--secondary" disabled={busy !== null} onClick={() => clock(r.id, 'out')}>
            {t('Clock out')}
          </button>
        </div>
      );
    }
    return null;
  };

  const stateLabel = (r) => {
    switch (r.state) {
      case 'working': return r.is_late ? t('Working (late {minutes} min)', { minutes: r.late_minutes || 0 }) : t('Working');
      case 'night': return t('Night shift since yesterday {time}', { time: hhmm(r.openShift.check_in_time) });
      case 'done': return t('Clocked out');
      case 'away': return r.leave_type_name || t(AWAY[r.status]);
      default: return t('Not in yet');
    }
  };

  if (loading && !board) {
    return <div className={styles.empty}>{t('Loading...')}</div>;
  }
  if (!board) {
    return (
      <div className={styles.empty}>
        <button type="button" className="btn btn--secondary" onClick={load}>{t('Retry')}</button>
      </div>
    );
  }

  const plannedCell = (r) => {
    const shifts = planOf(r.id);
    if (shifts.length === 0) return <span className={styles.muted}>—</span>;
    const since = notInSince(shifts, r.state, `${board.date}T${hhmm(board.now)}`);
    return (
      <>
        {shifts.map((s) => (
          <div key={s.id} className={styles.planLine}>
            {s.site_name}{' '}
            <span className={styles.muted}>
              {s.starts_at.slice(0, 10) < board.date ? t('yesterday {times}', { times: shiftTimes(s) }) : shiftTimes(s)}
            </span>
          </div>
        ))}
        {since && <div className={styles.planWarn}>{t('Planned from {time}, not in', { time: since })}</div>}
      </>
    );
  };

  const dateLabel = new Date(`${board.date}T00:00:00`).toLocaleDateString(locale, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div>
      <div className={styles.todayHeader}>
        <div>
          <div className={styles.todayDate}>{dateLabel}</div>
          <div className={styles.muted}>{t('Suriname time')} · {t('Updated at {time}', { time: hhmm(board.now) })}</div>
        </div>
        <button type="button" className="btn btn--secondary" onClick={load}>{t('Refresh')}</button>
      </div>

      <div className={styles.summary}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`${styles.summaryChip} ${filter === f.key ? styles.summaryChipActive : ''}`}
            onClick={() => setFilter(f.key)}
            aria-pressed={filter === f.key}
          >
            <span className={styles.summaryCount}>{f.key === 'all' ? rows.length : (counts[f.key] || 0)}</span>
            <span>{t(f.label)}</span>
          </button>
        ))}
      </div>

      {board.open_shifts.length > 0 && (
        <section className={styles.nightShifts} aria-label={t('Open night shifts')}>
          <h3 className={styles.sectionTitle}><Moon aria-hidden="true" /> {t('Open night shifts')}</h3>
          {board.open_shifts.map((s) => (
            <div key={s.attendance_id} className={styles.nightRow}>
              <div>
                <strong>{fullName(s)}</strong>
                <span className={styles.muted}> · {t('since yesterday {time}', { time: hhmm(s.check_in_time) })}</span>
              </div>
              {canClock && (
                <div className={styles.actionCell}>
                  {timeInput(s.employee_id)}
                  <button type="button" className="btn btn--secondary" disabled={busy !== null} onClick={() => clock(s.employee_id, 'out')}>
                    {t('Clock out')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      <input
        type="search"
        className={`input ${styles.search}`}
        placeholder={t('Search employee or position...')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label={t('Search employee or position...')}
      />

      <div className={styles.tableWrap}>
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('Employee')}</th>
              <th>{t('Status')}</th>
              {plan && <th>{t('Planned')}</th>}
              <th className="num">{t('In')}</th>
              <th className="num">{t('Out')}</th>
              <th className="num">{t('Hours')}</th>
              <th aria-label={t('Actions')} />
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.id} data-state={r.state}>
                <td>
                  <div className={styles.name}>{fullName(r)}</div>
                  <div className={styles.muted}>{r.position}</div>
                </td>
                <td><StatusBadge variant={r.state === 'away' ? statusVariant(r.status) : STATE_VARIANT[r.state]}>{stateLabel(r)}</StatusBadge></td>
                {plan && <td>{plannedCell(r)}</td>}
                <td className="num">{hhmm(r.check_in_time) || '—'}</td>
                <td className="num">{hhmm(r.check_out_time) || '—'}</td>
                <td className="num">{r.hours_worked != null && r.check_out_time ? `${Number(r.hours_worked).toFixed(2)} h` : '—'}</td>
                <td>{action(r)}</td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={plan ? 7 : 6} className={styles.empty}>{t('No employees match')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TodayTab;
